import React, { useState, useEffect } from 'react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Modal } from '../ui/Modal'
import { X, Save, Calendar, FileText } from 'lucide-react'

interface ProjectSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (data: { title: string; due_date: string | null }) => Promise<void>
  project: {
    id: string
    title: string
    due_date?: string
  }
  loading?: boolean
}

export const ProjectSettingsModal: React.FC<ProjectSettingsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  project,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    title: '',
    due_date: '',
  })
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  // Initialize form data when modal opens or project changes
  useEffect(() => {
    if (isOpen && project) {
      setFormData({
        title: project.title || '',
        due_date: project.due_date ? project.due_date.split('T')[0] : '', // Extract date part only
      })
      setErrors({})
    }
  }, [isOpen, project])

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Project title is required'
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'Project title must be at least 3 characters'
    } else if (formData.title.trim().length > 200) {
      newErrors.title = 'Project title must be less than 200 characters'
    }

    if (formData.due_date) {
      const dueDate = new Date(formData.due_date)
      const today = new Date()
      today.setHours(0, 0, 0, 0) // Reset time to start of day for comparison
      
      if (dueDate < today) {
        newErrors.due_date = 'Due date cannot be in the past'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      await onSave({
        title: formData.title.trim(),
        due_date: formData.due_date || null,
      })
      onClose()
    } catch (error) {
      console.error('Error saving project settings:', error)
    }
  }

  const handleClose = () => {
    if (!loading) {
      onClose()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="max-w-md">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Project Settings</h2>
              <p className="text-sm text-gray-600">Update project details</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={loading}
            className="p-1"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              label="Project Title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              error={errors.title}
              required
              disabled={loading}
              placeholder="Enter project title"
            />
          </div>

          <div>
            <Input
              label="Due Date"
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
              error={errors.due_date}
              disabled={loading}
              leftIcon={<Calendar className="w-4 h-4" />}
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty if no due date is required
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={loading || !formData.title.trim()}
            >
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default ProjectSettingsModal